/**
 * @file TillInvoiceActionModal.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-28
 * @modified 2026-09-28
 * @description The one modal behind Till Sales' manual invoicing actions, for a
 *              single basket or a bulk selection:
 *
 *                mark    — mark invoiced, optionally linking the invoice that covered it
 *                link    — link (or change) the invoice; marks it invoiced if it wasn't
 *                unlink  — clear the linked invoice, leaving it marked invoiced
 *                unmark  — undo a manual mark
 *
 *              Every mode takes an optional note, stored with the event in the
 *              transaction's audit history. The server decides per transaction
 *              what it may change (services/tillInvoicingService) and reports
 *              what it left alone; this modal only gathers the input.
 *
 *              The invoice picker searches as you type rather than loading a
 *              list: a till sale may be a walk-in with no account, so there is
 *              no one customer's invoices to offer, and there are ~100k in all.
 */

import React, { useState, useEffect, useRef } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Input from './Input';
import Textarea from './Textarea';
import { useSlideOutButton } from './SlideOutButton';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatDateShort } from '../utils/dateFormatters';
import './TillInvoiceActionModal.css';

const MODES = {
  mark: {
    title: 'Mark as invoiced',
    icon: 'task_alt',
    confirm: 'Mark Invoiced',
    intro: 'For a sale already billed some other way — it will not be offered for invoicing again. Link the invoice that covered it now, or later.',
    picker: 'optional'
  },
  link: {
    title: 'Link invoice',
    icon: 'link',
    confirm: 'Link Invoice',
    intro: 'The invoice that covered this sale. One not yet invoiced is marked invoiced as well.',
    picker: 'required'
  },
  unlink: {
    title: 'Unlink invoice',
    icon: 'link_off',
    confirm: 'Unlink',
    intro: 'Remove the linked invoice. The sale stays marked as invoiced.',
    picker: null
  },
  unmark: {
    title: 'Unmark as invoiced',
    icon: 'remove_done',
    confirm: 'Unmark',
    intro: 'Undo a manual mark: the sale goes back to not invoiced, and any linked invoice is unlinked. A sale invoiced by generating its invoice is undone by voiding that invoice instead.',
    picker: null
  }
};

/*
 * The body. Its own component so useSlideOutButton mounts with the footer
 * buttons, not with the always-mounted page (CLAUDE.md, Modal conventions).
 */
function ActionBody({ mode, groups, initialSearch, invoice, setInvoice, note, setNote, busy }) {
  useSlideOutButton([mode, !!invoice, busy]);
  const cfg = MODES[mode];
  const [search, setSearch] = useState(initialSearch || '');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    if (!cfg.picker) return undefined;
    const term = search.trim();
    if (term.length < 2) { setResults([]); return undefined; }
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const params = new URLSearchParams({ search: term, limit: '8', sortField: 'invoice_date', sortDirection: 'desc' });
        const res = await fetch(`${API_URL}/api/invoices/search?${params}`, { credentials: 'include' });
        const data = await res.json();
        // Only the answer to the latest keystroke is shown.
        if (mine === seq.current) setResults(Array.isArray(data.invoices) ? data.invoices : []);
      } catch (e) {
        if (mine === seq.current) setResults([]);
      } finally {
        if (mine === seq.current) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [search, cfg.picker]);

  const total = groups.reduce((sum, g) => sum + (Number(g.totalGross) || 0), 0);

  return (
    <>
      <p className="till-action-intro">{cfg.intro}</p>
      <div className="till-action-summary">
        <span className="material-symbols-rounded">point_of_sale</span>
        <span>
          {groups.length === 1
            ? `${groups[0].customerName} · ${formatCurrency(groups[0].totalGross)}`
            : `${groups.length} transactions · ${formatCurrency(total)}`}
        </span>
      </div>

      {cfg.picker && (
        <div className="till-action-field">
          <label>Invoice{cfg.picker === 'optional' ? ' (optional)' : ''}</label>
          {invoice ? (
            <div className="till-action-picked">
              <span className="material-symbols-rounded">receipt_long</span>
              <span className="picked-no">{invoice.invoice_no}</span>
              <span className="picked-meta">
                {invoice.company_name} · {formatDateShort(invoice.invoice_date)} · {formatCurrency(invoice.total)}
              </span>
              <button type="button" className="btn-icon" title="Choose another" onClick={() => setInvoice(null)}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
          ) : (
            <>
              <Input
                icon="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by invoice number, customer or account number…"
                autoFocus
              />
              <div className="till-action-results">
                {searching && <div className="till-action-empty">Searching…</div>}
                {!searching && search.trim().length >= 2 && results.length === 0 && (
                  <div className="till-action-empty">No invoices match.</div>
                )}
                {!searching && results.map(inv => (
                  <button type="button" key={inv.id} className="till-action-result" onClick={() => setInvoice(inv)}>
                    <span className="result-no">{inv.invoice_no}</span>
                    <span className="result-customer">{inv.account_no ? `${inv.account_no} · ` : ''}{inv.company_name}</span>
                    <span className="result-date">{formatDateShort(inv.invoice_date)}</span>
                    <span className="result-total">{formatCurrency(inv.total)}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <div className="till-action-field">
        <label>Note (optional)</label>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Kept in the transaction's history with this change"
          rows={2}
          maxLength={1000}
        />
      </div>
    </>
  );
}

/**
 * @param {Object}   props
 * @param {string}   props.mode      'mark' | 'link' | 'unlink' | 'unmark'
 * @param {Array}    props.groups    the Till Sales rows (baskets) acted on
 * @param {Function} props.onClose
 * @param {Function} props.onDone    called with the server's result once saved
 */
function TillInvoiceActionModal({ mode, groups, onClose, onDone }) {
  const cfg = MODES[mode] || MODES.mark;
  const [invoice, setInvoice] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // One customer across the selection: start the search on their account, so
  // their recent invoices are the first thing offered.
  const accounts = [...new Set(groups.map(g => g.accountNo).filter(Boolean))];
  const initialSearch = accounts.length === 1 ? accounts[0] : '';

  const endpoint = {
    mark: 'mark-invoiced',
    link: 'link-invoice',
    unlink: 'link-invoice',
    unmark: 'unmark-invoiced'
  }[mode];

  const submit = async () => {
    if (cfg.picker === 'required' && !invoice) return;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/epos-now/transactions/${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transactionIds: groups.flatMap(g => g.transactionIds || []),
          invoiceId: mode === 'unlink' ? null : (invoice ? invoice.id : null),
          note: note.trim()
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'The change could not be saved');
      onDone(data, { mode, invoice });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={busy ? () => {} : onClose}
      title={cfg.title}
      className="till-invoice-action"
      footer={
        <div className="buttons slideout">
          <div>
            <button className="btn-slideout" onClick={onClose} disabled={busy} data-read-safe>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button
              className="btn-slideout"
              onClick={submit}
              disabled={busy || (cfg.picker === 'required' && !invoice)}
            >
              <div className="icon-wrapper"><span className="material-symbols-rounded">{cfg.icon}</span></div>
              <span className="btn-label">{busy ? 'Saving…' : cfg.confirm}</span>
            </button>
          </div>
        </div>
      }
    >
      <ActionBody
        mode={mode}
        groups={groups}
        initialSearch={initialSearch}
        invoice={invoice}
        setInvoice={setInvoice}
        note={note}
        setNote={setNote}
        busy={busy}
      />
      {error && <p className="till-action-error">{error}</p>}
    </Modal>
  );
}

export default TillInvoiceActionModal;
